import { useState, useMemo, useEffect, useCallback, useContext, useRef } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useInView } from 'react-intersection-observer'
import { FormattedMessage, useIntl } from 'react-intl'

import Error404PageWithOffers from 'components/Pages/Error404PageWithOffers/Error404PageWithOffers'
import { FlightsClassTypes, fareClassUrlParser } from 'constants/flight'
import {
  CHARTER_BUNDLE_BADGE_MESSAGE,
  MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG,
  MALDIVES_CHARTER_PRIORITY_SUPPORT_NUMBER_HREF,
  MALDIVES_MARKETING_BANNER_IMAGE_ID,
  MALDIVES_DREAM_TILES,
} from 'constants/charterFlight'
import useCharterFlightLabels from 'hooks/Flights/useCharterFlightLabels'
import HeroImage from 'content/components/HeroImage'
import Pill from 'LuxKit/Pill'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import TabButton from 'LuxKit/Tabs/TabButton'
import TabButtonsGroup from 'LuxKit/Tabs/TabButtonsGroup'
import TextButton from 'LuxKit/Button/TextButton'
import Pane from 'components/Common/Pane'
import Modal from 'LuxKit/Modal/Modal'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineCompassIcon from 'LuxKit/Icons/line/LineCompassIcon'
import LineGuidedtoursIcon from 'LuxKit/Icons/line/LineGuidedtoursIcon'
import TextLink from 'LuxKit/TextLink'
import config from 'constants/config'
import Group from 'components/utils/Group'
import LayoutContainer from 'components/Common/LayoutContainer'
import ModalContext from 'contexts/ModalContext'
import MaldivesCharterFilters from './MaldivesDirectSearchFilters'
import MaldivesDirectSearchFlightCalendar from './MaldivesDirectCalendar/MaldivesDirectSearchFlightCalendar'
import OfferList, { OfferListAction } from 'components/OfferList/OfferList'
import useOfferList from 'hooks/Offers/useOfferList'
import useOffers from 'hooks/Offers/useOffers'
import HotelPriceToggle from 'components/Hotels/HotelPriceToggle'
import { parseAsBoolean, parseAsString, parseAsStringEnum, useQueryState } from 'nuqs'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import moment from 'moment'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { buildSuggestedDatesParamsKey, encodeChildrenOccupancy, parseOccupancyFromParams } from 'lib/search/searchUtils'
import { queryNullChildren } from 'constants/search'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import useFlightAndHotelBundle from 'hooks/FlightAndHotelBundle/useFlightAndHotelBundle'
import Places from 'constants/places'
import ReturnCharterFlightTile from 'components/Flights/CharterBundle/ReturnCharterFlightTile'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { setRecentlyUsedAirportCode } from 'actions/AuthActions'
import { OFFER_SET_SEARCH_RESULT_METADATA } from 'actions/actionConstants'
import { getDefaultAirport } from 'selectors/flightsSelectors'
import { useElementWidth } from 'hooks/useElementSize'
import {
  useMaldivesUspGroup1,
  useMaldivesUspGroup2,
  useMaldivesUspMobile,
  CharterPartnerIcon,
  MALDIVES_USP_IDS,
} from 'components/MaldivesCharter/MaldivesCharterUsp/MaldivesCharterUspItems'
import CharterBundleLandingPageUspItem from 'components/MaldivesCharter/MaldivesCharterUsp/CharterBundleLandingPageUspItem'
import { useFlightItemData } from './hooks/useFlightItemData'
import { buildBundleOfferFilters, sortMaldivesOfferIds, type MaldivesSortBy } from './MaldivesDirectUtils'
import { filterDateRangesByNights } from './hooks/utils'
import MaldivesDirectSearchFlightCalendarMobile from './MaldivesDirectCalendar/MaldivesDirectSearchFlightCalendarMobile'
import { LUXURY_ESCAPES } from 'constants/brands'
import MaldivesCallPrompt from './MaldivesCallPrompt'
import { agentHubOrSubBrandConfig } from 'agentHub/constants'
import useCharterDateRangesByMonth from 'hooks/CharterFlights/useCharterDateRangesByMonth'
import { EmptyArray } from 'lib/array/arrayUtils'
import CharterBundleCalendarActions from 'components/OfferPage/Filters/CharterFlight/CharterBundleCalendarActions'
import useCharterFlightDates from 'hooks/CharterFlights/useCharterFlightDates'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import { MALDIVES_CHARTER_ROUTE } from 'api/lib/flightDealMap'
import { isMaldivesCharterRegionEnabled } from 'selectors/maldivesSelectors'
import { MaldivesDirectBundleProvider } from './MaldivesDirectBundleContext'
import getOfferListKey from 'lib/offer/offerListKey'
import { useMaldivesOffersPricing, MaldivesOffersPricingContext } from './hooks/useMaldivesOffersPricing'
import { isSkyLoungeCompatible } from 'lib/flights/flightUtils'
import MaldivesCharterFlightFareClassModal from 'components/OfferPage/Filters/CharterFlight/MaldivesCharterFlightFareClassModal'
import {
  ALL_CHARTER_FARE_CLASSES,
  getCharterFareClassPolicy,
  getDefaultFareClassFromAllowed,
  getUnionAllowedFareClasses,
} from 'lib/charterFlights/charterOfferFareClassRestrictions'
import { OFFER_TYPE_TOUR, OFFER_TYPE_TOUR_V2 } from 'constants/offer'
import zIndex from 'styles/tools/z-index'
import { isAgentHubEnabled } from 'agentHub/selectors/agentHubSelectors'
import KidsFlyFreeTermsAndConditions from 'components/Common/KidsFlyFreeTermsAndConditions'
import DreamBlock from 'components/Pages/LandingPage/Hub/AdditionalGuides/DreamBlock'
import OfferSortControl from 'components/Common/OfferSortControl'
import useOfferListSortOptions from 'hooks/useOfferListSortOptions'
import { parseSearchString } from 'lib/url/searchUrlUtils'
import { replace } from 'connected-react-router'

type BundleType = '5-nights' | '12-nights'
type OfferMode = 'hotels' | 'tourBundles'

const URL_SORT_TO_MALDIVES_SORT: Record<string, MaldivesSortBy> = {
  'price.asc': 'price-low',
  'price.desc': 'price-high',
  'customerRating': 'rating',
  'value': 'value',
}
const MALDIVES_CHARTER_LANDING_PAGE_SORT = 'maldives-charter-landing-page'

const MELBOURNE_AIRPORT: App.Airport = {
  code: 'MEL',
  name: 'Tullamarine',
  latitude: -37.8136,
  longitude: 144.9631,
  countryCode: 'AU',
}

const StyledHeroImage = styled(HeroImage)`
  position: relative;
  min-height: ${rem(280)};
  justify-content: flex-start;

  /* Mobile: solid blue background with image at bottom */
  background-color: #0280B3;

  @media (max-width: 369px) {
    aspect-ratio: 3 / 2;
  }

  ${mediaQueryUp.tablet} {
    min-height: ${rem(400)};
    justify-content: center;
    background-color: transparent;
  }


  > div:first-child {
    ${mediaQueryUp.tablet} {
      top: 0;
    }
  }

  /* Blue gradient overlay */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    pointer-events: none;
    background: linear-gradient(0deg, rgba(2, 128, 179, 0.25) 0%, rgba(2, 128, 179, 0.65) 50%, rgba(2, 128, 179, 0.80) 100%);

    ${mediaQueryUp.tablet} {
      top: 0;
      background: linear-gradient(270deg, rgba(2, 128, 179, 0.00) 28.3%, rgba(2, 128, 179, 0.675) 89.24%);
    }
  }

`

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: ${rem(1280)};
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(24)};
  align-items: start;

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr 1fr;
    gap: ${rem(48)};
    margin-bottom: 0;
  }

  ${mediaQueryUp.desktop} {
    grid-template-columns: 1.5fr 1fr;
    gap: ${rem(64)};
  }
`

// Apply text shadow to all hero text for readability over background image
const HeroText = styled.div`
  & h1, & h2, & h3, & h4, & p, & span {
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  }
`

const CTACard = styled(Pane)`
  /* Pane already provides background-color via neutral-eight default */
  box-shadow: ${props => props.theme.shadow.bottom.small};
`

const CTACardMobile = styled(Pane)`
  position: absolute;
  left: 50%;
  bottom: 0;
  transform: translate(-50%, 50%);
  z-index: 1;
  min-width: ${rem(350)};
  box-shadow: ${props => props.theme.shadow.bottom.small};
`

// USP Section Styles
const USPSection = styled.section`
  background-color: ${props => props.theme.palette.highlight.primary.lightBackground};

  /* Add padding-top on mobile to accommodate overlapping CTACard */
  padding: ${rem(100)} 0 ${rem(32)} 0;

  ${mediaQueryUp.tablet} {
    padding: ${rem(32)} 0;
  }

  /* Control br visibility in section headings */
  br {
    display: block;

    ${mediaQueryUp.tablet} {
      display: none;
    }
  }

  /* Main heading: wrap on mobile, no wrap on tablet+ */
  > div > div > h2 {
    white-space: normal;

    ${mediaQueryUp.tablet} {
      white-space: nowrap;
    }
  }
`

const USPGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${rem(24)} ${rem(16)};

  ${mediaQueryUp.desktop} {
    grid-template-columns: repeat(4, 1fr);
    gap: ${rem(32)};
  }
`

const USPFooter = styled(Group)`
  margin-top: ${rem(40)};
  text-align: center;
`

const ModeToggleBottomSpacer = styled.div`
  height: ${rem(16)};
  background-color: ${props => props.theme.palette.highlight.primary.lightBackground};

  ${mediaQueryUp.tablet} {
    height: ${rem(28)};
  }
`

function MaldivesSupportPhoneNumber({ headingVariant = 'heading5' }: { headingVariant?: 'heading5' | 'heading3' }) {
  return (
    <CSSBreakpoint min="tablet">
      <TextLink
        href={MALDIVES_CHARTER_PRIORITY_SUPPORT_NUMBER_HREF}
        startIcon={<LinePhoneIcon />}
        size="large"
        underline={false}
        block
      >
        <Heading variant={headingVariant} as="span">
          {config.MALDIVES_CHARTER_PRIORITY_SUPPORT_NUMBER}
        </Heading>
      </TextLink>
    </CSSBreakpoint>
  )
}

// Offers Section Styles
const OffersSection = styled.section`
  padding: ${rem(32)} 0;
  background-color: ${props => props.theme.palette.neutral.default.eight};
`

const OffersContainer = styled(LayoutContainer)`
  max-width: ${rem(1200)};
  padding-left: 0;
  padding-right: 0;

  ${mediaQueryUp.tablet} {
    padding-left: ${rem(20)};
    padding-right: ${rem(20)};
  }
`

const OffersHeader = styled(Group)`
  margin-bottom: ${rem(24)};

  ${mediaQueryDown.tablet} {
    padding-left: ${rem(20)};
    padding-right: ${rem(20)};
  }
`

const OfferModeToggleWrapper = styled.div`
  position: relative;
  margin: 0 0 ${rem(16)};
  z-index: 1;

  ${mediaQueryUp.tablet} {
    position: absolute;
    top: 0;
    margin: 0;
    padding: 0 ${rem(12)};
    display: flex;
    justify-content: center;
    left: 50%;
    transform: translateY(-50%) translateX(-50%);
    transition: transform 0.3s, top 0.3s;
    z-index: 1;

    &.full-size {
      position: fixed;
      top: 0;
      transform: translateY(0) translateX(-50%);
      z-index: ${zIndex.stickyHeader};
    }
  }
`

const MobileOfferModeToggleStickyWrapper = styled(CSSBreakpoint)`
  position: sticky;
  top: 0;
  margin-left: ${rem(-12)};
  margin-right: ${rem(-12)};
  background-color: ${props => props.theme.palette.neutral.default.eight};
  z-index: ${zIndex.stickyHeader};
`

const OfferModeToggleInner = styled.div`
  position: relative;
`

const MobileModeToggleButtons = styled(Group)`
  width: 100%;
`

const MobileModeTabButton = styled(TabButton)`
  flex: 1;
  min-width: 0;
`

const OfferModeTogglePane = styled(Pane)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  ${mediaQueryUp.tablet} {
    box-shadow: ${props => props.theme.shadow.bottom.medium};
    border-radius: 2px;
    transform: scaleX(1);
    transition: transform 0.3s;
    transform-origin: center top;

    &.full-size {
      transform: scaleX(var(--menu-scale, 1));
    }
  }
`

const ContactBannerContainer = styled(LayoutContainer)`
  margin-top: ${rem(30)};
`

const MALDIVES_DIRECT_OFFERS_SECTION_ID = 'maldives-direct-offers-heading'
const isMelbourneAirport = (airport?: App.Airport) => ['AVV', 'MBW', 'MEB', 'MELALL'].includes(airport?.code ?? 'MEL')

interface ModeToggleTabsProps {
  variant: 'desktop' | 'mobile'
  isTourBundleMode: boolean
  onHotelsModeClick: () => void
  onTourBundlesModeClick: () => void
  showFullSize?: boolean
  modeToggleScale?: number
  modeToggleRef?: React.RefObject<HTMLDivElement | null>
}

function ModeToggleTabs({
  variant,
  isTourBundleMode,
  onHotelsModeClick,
  onTourBundlesModeClick,
  showFullSize = false,
  modeToggleScale = 1,
  modeToggleRef,
}: ModeToggleTabsProps) {
  const intl = useIntl()
  const isMobile = variant === 'mobile'
  const hotelsMobileLabel = intl.formatMessage({
    id: 'qOtWA4_OicqojNu_wYq0',
    defaultMessage: 'Hotel + flight',
    description: 'Mode toggle label for hotel + flight on mobile in the maldives direct search page section',
  })
  const hotelsDesktopLabel = intl.formatMessage({
    id: 'fUfszU5WERwX82Ju-mM8',
    defaultMessage: 'Hotel + flight bundles',
    description: 'Mode toggle label for hotel + flight bundles on desktop',
  })
  const toursMobileLabel = intl.formatMessage({
    id: 'E-Mjs5oUdSQ9m6QW70la',
    defaultMessage: 'Tour + flight',
    description: 'Mode toggle label for tour + flight on mobile in the maldives direct search page section',
  })
  const toursDesktopLabel = intl.formatMessage({
    id: 's3G4_TeOzBMstPBo07c5',
    defaultMessage: 'Tour + flight bundles',
    description: 'Mode toggle label for tour + flight bundles on desktop',
  })
  const hotelsLabel = isMobile ? hotelsMobileLabel : hotelsDesktopLabel
  const toursLabel = isMobile ? toursMobileLabel : toursDesktopLabel

  if (isMobile) {
    return (
      <OfferModeToggleInner>
        <MobileModeToggleButtons direction="horizontal" gap={0}>
          <MobileModeTabButton
            size="medium"
            isActive={!isTourBundleMode}
            startIcon={<LineBedIcon />}
            onClick={onHotelsModeClick}
          >
            {hotelsLabel}
          </MobileModeTabButton>
          <MobileModeTabButton
            size="medium"
            isActive={isTourBundleMode}
            startIcon={<LineCompassIcon />}
            onClick={onTourBundlesModeClick}
          >
            {toursLabel}
          </MobileModeTabButton>
        </MobileModeToggleButtons>
      </OfferModeToggleInner>
    )
  }

  return (
    <OfferModeToggleInner ref={modeToggleRef}>
      <OfferModeTogglePane
        className={showFullSize ? 'full-size' : undefined}
        style={{ '--menu-scale': modeToggleScale } as React.CSSProperties}
      />
      <TabButtonsGroup fit="grow" tabletFit="auto">
        <TabButton
          size="medium"
          isActive={!isTourBundleMode}
          startIcon={<LineBedIcon />}
          onClick={onHotelsModeClick}
        >
          {hotelsLabel}
        </TabButton>
        <TabButton
          size="medium"
          isActive={isTourBundleMode}
          startIcon={<LineCompassIcon />}
          onClick={onTourBundlesModeClick}
        >
          {toursLabel}
        </TabButton>
      </TabButtonsGroup>
    </OfferModeToggleInner>
  )
}

function MaldivesDirectSearchPage() {
  const intl = useIntl()
  const { charterBundleDurationOptions } = useCharterFlightLabels()
  const uspGroup1 = useMaldivesUspGroup1()
  const uspGroup2 = useMaldivesUspGroup2()
  const mobileUsps = useMaldivesUspMobile()
  const isRegionSupported = useAppSelector(isMaldivesCharterRegionEnabled)
  const windowSearch = useAppSelector(state => state.router.location.search)
  const maldivesCharterEnabled = isRegionSupported && (config.BRAND === LUXURY_ESCAPES || agentHubOrSubBrandConfig.isEnabled)
  const isDesktop = useScreenSizeUp('desktop')
  const dispatch = useAppDispatch()
  const openModal = useContext(ModalContext)

  // Detect when user has scrolled past the hero banner
  const { ref: heroDetectorRef, inView: heroInView } = useInView({
    threshold: 0,
    initialInView: true,
  })

  // State to track if user has manually closed the prompt
  const [isCallPromptClosed, setIsCallPromptClosed] = useState(false)
  const modeToggleRef = useRef<HTMLDivElement | null>(null)
  const modeToggleWidth = useElementWidth(modeToggleRef as React.MutableRefObject<HTMLElement | null>)
  const [viewportWidth, setViewportWidth] = useState(0)

  // Flight and Hotel bundle management
  const flightAndHotelBundle = useFlightAndHotelBundle()

  // Occupancy query params - encoded as two separate query params (adults, children)
  // Default: 2 adults, no children (encoded as "2" for adults, "none" for children)
  const [adultsParam, setAdultsParam] = useQueryState('adults', parseAsString.withDefault('2'))
  const [childrenParam, setChildrenParam] = useQueryState('children', parseAsString.withDefault(queryNullChildren))

  // Parse the query params into rooms array, ensuring teenagers field is included for consistency
  const rooms = useMemo<Array<App.Occupants>>(
    () => parseOccupancyFromParams(adultsParam, childrenParam).map(room => ({
      ...room,
      teenagers: room.teenagers ?? 0,
    })),
    [adultsParam, childrenParam],
  )

  // Helper function to update rooms (updates both query params)
  const setRooms = useCallback((newRooms: Array<App.Occupants>) => {
    const adultsString = newRooms.map(room => room.adults).join(',')
    const childrenString = newRooms.map(room => encodeChildrenOccupancy(room.childrenAge)).join(',')

    setAdultsParam(adultsString)
    setChildrenParam(childrenString)
  }, [setAdultsParam, setChildrenParam])

  // Airport query params - explicit string parsers, use recentlyUsedAirportCode if available
  const departureAirportOfUser = useAppSelector(getDefaultAirport)

  const isMelbourneDefaultAirport = isMelbourneAirport(departureAirportOfUser) || agentHubOrSubBrandConfig.isEnabled
  const [departureAirport, setDepartureAirport] = useState<App.Airport>(isMelbourneDefaultAirport ? MELBOURNE_AIRPORT : departureAirportOfUser ?? MELBOURNE_AIRPORT)

  // sync airport to redux when agent hub is enabled so offer pages inherit the correct airport
  useEffect(() => {
    if (agentHubOrSubBrandConfig.isEnabled && departureAirport.code === MELBOURNE_AIRPORT.code) {
      dispatch(setRecentlyUsedAirportCode(MELBOURNE_AIRPORT.code))
    }
  }, [departureAirport.code, dispatch])

  // Flight class - custom parser that maps URL values (e.g., 'economy-plus') to internal types
  const [fareClassParam, setFareClassParam] = useQueryState(
    'fareClass',
    fareClassUrlParser,
  )
  const fareClass = fareClassParam ?? undefined
  const fareClassForSearch = fareClass ?? FlightsClassTypes.ECONOMY

  // Bundle selection - '5-nights' or '12-nights'
  const [selectedBundle, setSelectedBundle] = useQueryState(
    'bundle',
    parseAsStringEnum<BundleType>(['5-nights', '12-nights'] as const).withDefault('5-nights'),
  )

  // LuxPlus toggle
  // Default to false if brand is not Luxury Escapes
  const [luxPlusEnabled, setLuxPlusEnabled] = useQueryState(
    'luxPlus',
    parseAsBoolean.withDefault(config.BRAND === LUXURY_ESCAPES),
  )

  // Price display mode state (persisted in URL)
  const [priceMode, setPriceMode] = useQueryState(
    'priceMode',
    parseAsStringEnum<'total' | 'perPerson'>(['total', 'perPerson'] as const).withDefault('total'),
  )
  const [offerMode, setOfferMode] = useQueryState(
    'offerMode',
    parseAsStringEnum<OfferMode>(['hotels', 'tourBundles'] as const).withDefault('hotels'),
  )
  const isTourBundleModeRequested = offerMode === 'tourBundles'
  const modeToggleScale = useMemo(() => {
    if (!modeToggleWidth) {
      return 1
    }
    return viewportWidth / modeToggleWidth
  }, [modeToggleWidth, viewportWidth])
  const tourModeFlexibleBookingsDescription = intl.formatMessage({
    id: 'T-oMECDw_mbOzxhlW-NL',
    defaultMessage: 'Experience peace of mind with free cancellation within 7 days of purchase. Conditions apply.',
    description: 'Tour mode USP description for flexible bookings in the maldives direct search page section',
  })
  const tourModeHolidayTimeTitle = intl.formatMessage({
    id: 'eTIClOro5ID6J9UaD9wS',
    defaultMessage: 'Expert-led itineraries',
    description: 'Tour mode USP title replacing maximise your holiday time',
  })
  const tourModeHolidayTimeDescription = intl.formatMessage({
    id: 'QxO2t5wqs4v5KaMaR06I',
    defaultMessage: 'Thoughtfully designed tours that combine must-see destinations with hidden gems, so you get the most from every day.',
    description: 'Tour mode USP description for expert-led itineraries',
  })
  const tourModeFlexibleBookingsMobileDescription = intl.formatMessage({
    id: 'HtgpFWLYUSKUumIcV_d9',
    defaultMessage: '<strong>Free cancellation</strong> within 7 days of purchase. Conditions apply.',
    description: 'Tour mode mobile USP description for flexible bookings with bold lead-in',
  }, {
    strong: chunks => <strong>{chunks}</strong>,
  })
  const tourModeHolidayTimeMobileDescription = intl.formatMessage({
    id: 'p0VAyYxnmEXOf-Ea1mRa',
    defaultMessage: '<strong>Expert-led itineraries</strong> combining must-see destinations with hidden gems.',
    description: 'Tour mode mobile USP description for expert-led itineraries with bold lead-in',
  }, {
    strong: chunks => <strong>{chunks}</strong>,
  })
  const tourModeBundlesHeading = intl.formatMessage({
    id: 'w-ht91OUqcLgFTVsFBvg',
    defaultMessage: 'Exclusive tour + flight bundles',
    description: 'Heading shown above offers list when tour mode is active',
  })

  const pageSize = 25

  const [checkInDateParam, setCheckInDate] = useQueryState('checkIn', parseAsString)
  const [checkOutDateParam, setCheckOutDate] = useQueryState('checkOut', parseAsString)

  const checkInDate = checkInDateParam ?? undefined
  const checkOutDate = checkOutDateParam ?? undefined

  // Flight calendar modal state
  const [isFlightCalendarOpen, setIsFlightCalendarOpen] = useState(false)

  const [selectedMonth, setSelectedMonth] = useState<string>('')

  // Handle price mode change
  function handlePriceModeChange(newMode: 'total' | 'perPerson') {
    setPriceMode(newMode)
  }

  const handleHotelsModeClick = useCallback(() => {
    setOfferMode('hotels')
  }, [setOfferMode])

  const handleTourBundlesModeClick = useCallback(() => {
    setOfferMode('tourBundles')
  }, [setOfferMode])

  const handleBrowseAllOffersClick = useCallback(() => {
    document
      .getElementById(MALDIVES_DIRECT_OFFERS_SECTION_ID)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  useEffect(() => {
    setViewportWidth(window.innerWidth)

    const handleViewportResize = () => {
      const nextViewportWidth = window.innerWidth
      setViewportWidth(previousViewportWidth => (
        previousViewportWidth === nextViewportWidth ? previousViewportWidth : nextViewportWidth
      ))
    }
    window.addEventListener('resize', handleViewportResize)

    return () => window.removeEventListener('resize', handleViewportResize)
  }, [])

  // Handle flight calendar modal
  function handleOpenFlightCalendar() {
    setIsFlightCalendarOpen(true)
  }

  function handleCloseFlightCalendar() {
    setIsFlightCalendarOpen(false)
  }

  function handleBundleChange(bundle: BundleType) {
    setSelectedBundle(bundle)
    // Update dates based on bundle selection
    const nights = bundle === '5-nights' ? 5 : 12
    const newCheckoutDate = moment(checkInDate).add(nights, 'days').format(ISO_DATE_FORMAT)
    setCheckOutDate(newCheckoutDate)
  }

  function handleAirportChange(airport: App.Airport) {
    setDepartureAirport(airport)
    dispatch(setRecentlyUsedAirportCode(airport.code))
  }

  function handleFlightClassChange(flightClass: FlightsClassTypes) {
    setFareClassParam(flightClass)
  }

  // Check SkyLounge compatibility when rooms are applied (not on every intermediate change)
  const checkAndResetFareClassIfNeeded = useCallback((appliedRooms: Array<App.Occupants>) => {
    if (fareClass === FlightsClassTypes.LUXROW && !isSkyLoungeCompatible(appliedRooms)) {
      setFareClassParam(FlightsClassTypes.ECONOMY)
    }
  }, [fareClass, setFareClassParam])

  function handleLuxPlusToggle(enabled: boolean) {
    setLuxPlusEnabled(enabled)
  }

  function handleDateSelect(departDate: string, returnDate: string) {
    setCheckInDate(departDate)
    setCheckOutDate(returnDate)
  }

  // Handle flight calendar date selection (from modal)
  function handleFlightDateSelect(option: { departDate: string, returnDate: string, checkIn: string, checkOut: string }) {
    // Use checkIn and checkOut (inner dates) for hotel queries
    setCheckInDate(option.checkIn)
    setCheckOutDate(option.checkOut)
    handleCloseFlightCalendar()
  }

  function handleMonthChange(month: string) {
    setSelectedMonth(month)
  }

  // Handle duration change from flight calendar
  function handleDurationChangeFromCalendar(nights: number) {
    const bundle = nights === 5 ? '5-nights' : '12-nights'
    setSelectedBundle(bundle)

    // Update checkout date based on new duration
    const newCheckoutDate = moment(checkInDate).add(nights, 'days').format(ISO_DATE_FORMAT)
    setCheckOutDate(newCheckoutDate)
  }

  // Calculate nights based on selected bundle
  const nights = selectedBundle === '5-nights' ? 5 : 12

  // Get charter flight dates (departDate/returnDate) from dateRangesByMonth
  const { departDate, returnDate } = useCharterFlightDates({
    checkInDate,
    nights,
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    rooms,
    fareClass: fareClassForSearch,
  })

  const searchParams = useMemo(() => parseSearchString(windowSearch), [windowSearch])
  useEffect(() => {
    let didUpdateParams = false
    const params = new URLSearchParams(windowSearch)

    if (searchParams.holidayTypes) {
      params.delete('holidayTypes')
      didUpdateParams = true
    }

    if (didUpdateParams) {
      dispatch(replace({ search: params.toString() }))
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isRecommendedSort = !searchParams.sortBy || searchParams.sortBy === 'recommended' || searchParams.sortBy === MALDIVES_CHARTER_LANDING_PAGE_SORT
  const currentSortBy: MaldivesSortBy = URL_SORT_TO_MALDIVES_SORT[searchParams.sortBy ?? ''] ?? 'price-low'
  const {
    SORT_OPTION_RECOMMENDED,
    SORT_OPTION_VALUE,
    SORT_OPTION_PRICE_ASC,
    SORT_OPTION_PRICE_DESC,
    SORT_OPTION_CUSTOMER_RATING,
  } = useOfferListSortOptions()
  const maldivesSortOptions = useMemo(() => [
    SORT_OPTION_RECOMMENDED,
    SORT_OPTION_PRICE_ASC,
    SORT_OPTION_VALUE,
    SORT_OPTION_PRICE_DESC,
    SORT_OPTION_CUSTOMER_RATING,
  ], [SORT_OPTION_RECOMMENDED, SORT_OPTION_PRICE_ASC, SORT_OPTION_VALUE, SORT_OPTION_PRICE_DESC, SORT_OPTION_CUSTOMER_RATING])
  const handleSortChange = useCallback(({ search }: { search: string }) => {
    dispatch(replace({ search }))
  }, [dispatch])

  const bundleFilters = useMemo<App.OfferListFilters>(() => {
    const filters = buildBundleOfferFilters({
      destinationId: Places.Maldives.id,
      destinationName: Places.Maldives.name,
      checkIn: checkInDate ?? '', // Light search ignores these anyway
      checkOut: checkOutDate ?? '',
      rooms: rooms,
      fareClass: fareClassForSearch,
    })
    return {
      ...filters,
      isStream: false,
      limit: pageSize,
      departureAirportCode: departureAirport.code !== MELBOURNE_AIRPORT.code ? departureAirport.code : undefined,
      ...(isRecommendedSort ? {
        sortBy: MALDIVES_CHARTER_LANDING_PAGE_SORT as App.OfferListSortByOptions,
        useUnifiedSearchEndpoint: true,
      } : {}),
    }
  }, [checkInDate, checkOutDate, rooms, fareClassForSearch, pageSize, departureAirport.code, isRecommendedSort])

  const tourBundleFilters = useMemo<App.OfferListFilters>(() => ({
    offerTypes: [OFFER_TYPE_TOUR, OFFER_TYPE_TOUR_V2],
    campaigns: [MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG],
    flexibleMonths: '',
    flexibleNights: '',
    limit: pageSize,
    isStream: false,
    showEnrichedResult: true,
  }), [pageSize])
  // Keep this request enabled in both modes to know whether to render the tour toggle.
  const tourBundleOfferList = useOfferList(tourBundleFilters, {
    minRequiredOffers: pageSize,
  })
  const hasTourBundleOffers = tourBundleOfferList.offerIds.length > 0
  const isTourBundleMode = isTourBundleModeRequested && hasTourBundleOffers
  const maldivesOfferList = useOfferList(bundleFilters, {
    minRequiredOffers: pageSize,
    clientCheckAvailability: true,
    disabled: isTourBundleMode,
  })
  const [offers, offersFetching] = useOffers<App.HotelOffer>(maldivesOfferList.offerIds)
  const isOffersLoading = useMemo(() => maldivesOfferList.fetching || offersFetching, [maldivesOfferList.fetching, offersFetching])
  const displayedUspsCombined = useMemo(() => {
    const uspsCombined = [...uspGroup1, ...uspGroup2]
    if (!isTourBundleMode) {
      return uspsCombined
    }

    return uspsCombined.map(uspItem => {
      if (uspItem.uspId === MALDIVES_USP_IDS.flexibleBookings) {
        return { ...uspItem, description: tourModeFlexibleBookingsDescription }
      }
      if (uspItem.uspId === MALDIVES_USP_IDS.maximiseHolidayTime) {
        return {
          ...uspItem,
          title: tourModeHolidayTimeTitle,
          description: tourModeHolidayTimeDescription,
          icon: <LineGuidedtoursIcon />,
          tabletIcon: <LineGuidedtoursIcon size="XL" style={{ display: 'block' }} />,
        }
      }
      return uspItem
    })
  }, [uspGroup1, uspGroup2, isTourBundleMode, tourModeFlexibleBookingsDescription, tourModeHolidayTimeTitle, tourModeHolidayTimeDescription])
  const displayedMobileUsps = useMemo(() => {
    if (!isTourBundleMode) {
      return mobileUsps
    }

    return mobileUsps.map(uspItem => {
      if (uspItem.uspId === MALDIVES_USP_IDS.flexibleBookings) {
        return {
          ...uspItem,
          description: tourModeFlexibleBookingsMobileDescription,
        }
      }
      if (uspItem.uspId === MALDIVES_USP_IDS.maximiseHolidayTime) {
        return {
          ...uspItem,
          title: tourModeHolidayTimeTitle,
          description: tourModeHolidayTimeMobileDescription,
          icon: <LineGuidedtoursIcon />,
        }
      }
      return uspItem
    })
  }, [mobileUsps, isTourBundleMode, tourModeFlexibleBookingsMobileDescription, tourModeHolidayTimeTitle, tourModeHolidayTimeMobileDescription])
  const allowedFareClassesByOfferId = useMemo(() => {
    const map = new Map<string, Array<FlightsClassTypes>>()
    offers.forEach((offer) => {
      if (!offer) {
        return
      }
      const policy = getCharterFareClassPolicy({
        campaignTags: offer.campaignTags,
        occupancies: rooms,
      })
      map.set(offer.id, policy.allowedFareClasses)
    })
    return map
  }, [offers, rooms])
  const hasFareClassRestrictionsByOfferId = useMemo(() => {
    const map = new Map<string, boolean>()
    offers.forEach((offer) => {
      if (!offer) {
        return
      }
      const policy = getCharterFareClassPolicy({
        campaignTags: offer.campaignTags,
        occupancies: rooms,
      })
      map.set(offer.id, policy.taggedFareClasses.length > 0)
    })
    return map
  }, [offers, rooms])
  const availableFareClasses = useMemo(() => {
    const union = getUnionAllowedFareClasses({ offers, occupancies: rooms })
    if (union.length > 0) {
      return union
    }
    const policy = getCharterFareClassPolicy({
      campaignTags: [MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG],
      occupancies: rooms,
    })
    return policy.allowedFareClasses
  }, [offers, rooms])
  const defaultAvailableFareClass = useMemo(
    () => getDefaultFareClassFromAllowed(availableFareClasses),
    [availableFareClasses],
  )
  const effectiveAvailableFareClasses = useMemo(() => {
    if (availableFareClasses.length > 0) {
      return availableFareClasses
    }
    return ALL_CHARTER_FARE_CLASSES
  }, [availableFareClasses])
  const canOpenFareClassModal = useMemo(
    () => effectiveAvailableFareClasses.length > 1,
    [effectiveAvailableFareClasses.length],
  )
  const openFareClassModal = useCallback(async() => {
    if (!canOpenFareClassModal) {
      return
    }
    const selectedClass = await openModal<FlightsClassTypes>(<MaldivesCharterFlightFareClassModal
      selectedClassId={fareClassForSearch}
      checkInDate={checkInDate}
      checkOutDate={checkOutDate}
      occupancies={rooms}
      allowedFareClasses={effectiveAvailableFareClasses}
    />)
    if (selectedClass) {
      setFareClassParam(selectedClass)
    }
  }, [canOpenFareClassModal, openModal, fareClassForSearch, checkInDate, checkOutDate, rooms, effectiveAvailableFareClasses, setFareClassParam])
  const bundleContextValue = useMemo(() => ({
    charterBundle: true,
    isPricePerPerson: priceMode === 'perPerson',
    fareClass: fareClassForSearch,
    duration: nights,
    allowedFareClassesByOfferId,
    hasFareClassRestrictionsByOfferId,
    canOpenFareClassModal,
    openFareClassModal,
  }), [priceMode, fareClassForSearch, nights, allowedFareClassesByOfferId, hasFareClassRestrictionsByOfferId, canOpenFareClassModal, openFareClassModal])
  const listKey = useMemo(() => bundleFilters ? getOfferListKey(bundleFilters) : '', [bundleFilters])
  const rawOfferListFromState = useAppSelector(state => listKey ? state.offer.offerLists[listKey] : undefined)
  const resolvedEmpty = useMemo(
    () => !isOffersLoading && rawOfferListFromState?.fetching === false && (rawOfferListFromState?.offerCount === 0),
    [isOffersLoading, rawOfferListFromState?.fetching, rawOfferListFromState?.offerCount],
  )
  const [lastNonEmptyOfferList, setLastNonEmptyOfferList] = useState<App.OfferList | undefined>(undefined)
  useEffect(() => {
    if (!maldivesOfferList.fetching && maldivesOfferList.offerIds.length > 0) {
      setLastNonEmptyOfferList(maldivesOfferList)
    }
  }, [maldivesOfferList, maldivesOfferList.fetching, maldivesOfferList.offerIds])
  useEffect(() => {
    if (!defaultAvailableFareClass) {
      return
    }
    if (fareClass && !availableFareClasses.includes(fareClass)) {
      setFareClassParam(defaultAvailableFareClass)
    }
  }, [availableFareClasses, defaultAvailableFareClass, fareClass, setFareClassParam])

  useEffect(() => {
    if (isTourBundleModeRequested && !tourBundleOfferList.fetching && !hasTourBundleOffers) {
      setOfferMode('hotels')
    }
  }, [isTourBundleModeRequested, tourBundleOfferList.fetching, hasTourBundleOffers, setOfferMode])
  useEffect(() => {
    if (isTourBundleMode && isFlightCalendarOpen) {
      setIsFlightCalendarOpen(false)
    }
  }, [isTourBundleMode, isFlightCalendarOpen])

  // Update Redux suggested dates when check-in/check-out changes so offer URLs use correct dates
  useEffect(() => {
    if (isTourBundleMode || !checkInDate || !checkOutDate || maldivesOfferList.offerIds.length === 0) {
      return
    }

    const suggestedDatesKey = buildSuggestedDatesParamsKey('', '', rooms)
    const offerSuggestedDates = maldivesOfferList.offerIds.reduce<Record<string, App.OfferSuggestedDates>>((acc, offerId) => {
      acc[offerId] = {
        checkIn: checkInDate,
        checkOut: checkOutDate,
      }
      return acc
    }, {})

    // Use existing OFFER_SET_SEARCH_RESULT_METADATA action to update suggested dates
    // Using unique keys to avoid conflicts with other pages:
    // - searchTargetId: Use a unique prefix to avoid overwriting other pages' data
    // - Empty objects for other fields won't overwrite existing data due to reducer merge logic
    const uniqueSearchTargetId = `maldives-direct-${Places.Maldives.id}`
    const uniqueAvailabilityKey = `maldives-direct-${suggestedDatesKey}`
    const uniqueOfferMetaDataKey = `maldives-direct-${maldivesOfferList.key || ''}`

    dispatch({
      type: OFFER_SET_SEARCH_RESULT_METADATA,
      data: {
        distanceFromSearchTarget: {
          [uniqueSearchTargetId]: {},
        },
        offerAvailabilityFromSearchTarget: {
          [uniqueAvailabilityKey]: {},
        },
        offerSuggestedDates: {
          [suggestedDatesKey]: offerSuggestedDates,
        },
        offerMetaData: {
          [uniqueOfferMetaDataKey]: {},
        },
      },
      searchTargetId: uniqueSearchTargetId,
      availabilityKey: uniqueAvailabilityKey,
      suggestedDatesKey,
      offerMetaDataKey: uniqueOfferMetaDataKey,
    })
  }, [dispatch, isTourBundleMode, checkInDate, checkOutDate, maldivesOfferList.offerIds, maldivesOfferList.key, rooms])

  const mainFlightData = useFlightItemData({
    checkIn: checkInDate ?? '',
    checkOut: checkOutDate ?? '',
    rooms,
    fareClass: fareClassForSearch,
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
  })

  // Get dateRangesByMonth from charter flight journeys API
  // Use fixed date range to fetch all available flights, then extract all dates from the response
  const { dateRangesByMonth: allDateRangesByMonth } = useCharterDateRangesByMonth({
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    occupancies: rooms ?? EmptyArray,
    fareClass: fareClassForSearch,
    disabled: (rooms ?? EmptyArray).length === 0 || isTourBundleMode,
  })

  // Filter dateRangesByMonth by selected nights
  const dateRangesByMonth = useMemo(
    () => filterDateRangesByNights(allDateRangesByMonth, nights),
    [allDateRangesByMonth, nights],
  )

  useEffect(() => {
    if (checkInDate) {
      setSelectedMonth(moment(checkInDate).format('MMM'))
    } else {
      const months = Object.keys(dateRangesByMonth)
      if (months.length > 0) {
        setSelectedMonth(months[0])
      }
    }
  }, [checkInDate, dateRangesByMonth])

  // Calculate per-offer pricing using calendar + flight data
  const offersPricingData = useMaldivesOffersPricing({
    offers,
    rooms,
    dateRangesByMonth,
    selectedFareClass: fareClass,
    searchFareClass: fareClassForSearch,
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    selectedCheckIn: checkInDate ?? undefined,
    selectedCheckOut: checkOutDate ?? undefined,
    nights,
  })

  const offersById = useMemo(() => {
    const map: Record<string, App.HotelOffer | undefined> = {}
    for (const offer of offers) {
      if (offer) map[offer.id] = offer
    }
    return map
  }, [offers])
  const campaignFilteredOfferIds = useMemo(() => maldivesOfferList.offerIds.filter((offerId) => {
    const offer = offersById[offerId]
    // Keep IDs until offer data is hydrated; once loaded, enforce campaign-tag membership.
    return !offer || offer.campaignTags.includes(MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG)
  }), [maldivesOfferList.offerIds, offersById])

  const sortedOfferIds = useMemo(() => {
    if (maldivesOfferList.fetching || offersFetching || offers.length === 0) {
      return campaignFilteredOfferIds
    }
    if (isRecommendedSort) {
      return campaignFilteredOfferIds
    }
    return sortMaldivesOfferIds(
      campaignFilteredOfferIds,
      currentSortBy,
      offersPricingData.pricingByOfferId,
      offersById,
    )
  }, [campaignFilteredOfferIds, maldivesOfferList.fetching, offersFetching, offers, isRecommendedSort, offersPricingData, offersById, currentSortBy])
  const displayOfferList = useMemo<App.OfferList>(() => {
    if (resolvedEmpty) return maldivesOfferList
    if (sortedOfferIds.length > 0 || !isOffersLoading) {
      return {
        ...maldivesOfferList,
        offerIds: sortedOfferIds,
      }
    }
    return lastNonEmptyOfferList ?? maldivesOfferList
  }, [resolvedEmpty, maldivesOfferList, sortedOfferIds, lastNonEmptyOfferList, isOffersLoading])

  // TODO: i18n flagged — concatenated string with pluralizeToString
  const offersHeaderNode = useMemo(() => {
    if (isOffersLoading) {
      return <BodyText variant="large">
        {intl.formatMessage({
          id: 'Gwf6vZLx0VkVILuxpBxT',
          defaultMessage: 'Loading offers…',
          description: 'Loading text while Maldives charter hotel offers are being fetched',
        })}
      </BodyText>
    }
    return (
      <BodyText variant="large">
        {intl.formatMessage(
          {
            id: '0gwKsC-kP5LNQpR66-Kr',
            defaultMessage: 'Showing {count, plural, one {# offer} other {# offers}}',
            description: 'Header text showing number of Maldives charter hotel offers found',
          },
          { count: displayOfferList.offerIds.length },
        )}
      </BodyText>
    )
  }, [isOffersLoading, displayOfferList.offerIds.length, intl])
  // TODO: i18n flagged — concatenated string with pluralizeToString
  const tourOffersHeaderNode = useMemo(() => {
    if (tourBundleOfferList.fetching) {
      return <BodyText variant="large">
        {intl.formatMessage({
          id: 'OHpjLwCvgADt7aUGbNJ7',
          defaultMessage: 'Loading offers…',
          description: 'Loading text while Maldives charter tour offers are being fetched',
        })}
      </BodyText>
    }
    return (
      <BodyText variant="large">
        {intl.formatMessage(
          {
            id: 'hG3y5JBXM9U8EbMe0yYX',
            defaultMessage: 'Showing {count, plural, one {# offer} other {# offers}}',
            description: 'Header text showing number of Maldives charter tour offers found',
          },
          { count: tourBundleOfferList.offerIds.length },
        )}
      </BodyText>
    )
  }, [tourBundleOfferList.fetching, tourBundleOfferList.offerIds.length, intl])

  // Sync bundle data to Redux store (only when dates are selected)
  useEffect(() => {
    if (isTourBundleMode) {
      return
    }
    if (checkInDate && checkOutDate) {
      flightAndHotelBundle.updateSelectedDates({
        checkIn: checkInDate,
        checkOut: checkOutDate,
      })
    }

    flightAndHotelBundle.updateFlightData({
      fetching: mainFlightData.fetching,
      totalPrice: mainFlightData.totalFlightFare,
      journey: mainFlightData.departingFlight,
    })
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [isTourBundleMode, checkInDate, checkOutDate, mainFlightData])

  const exclusiveToLEText = intl.formatMessage(CHARTER_BUNDLE_BADGE_MESSAGE)

  const heroHeadingMessage = (
    <FormattedMessage
      id="_6GHeVTwxFe2IQARogy1"
      defaultMessage="The Maldives, {br}with <em> more </em> time in paradise"
      description="Hero heading for Maldives Direct search page in the search interface"
      values={{
        em: (chunks) => <em>{chunks}</em>,
        br: <br />,
      }}
    />
  )
  const hurryLimitedSeatsText = intl.formatMessage({
    id: 'DzgicMX1YYZOLqMlI8eK',
    defaultMessage: 'Hurry, limited seats!',
    description: 'Urgency heading on Maldives charter hero CTA card in the maldives direct search page section',
  })
  const callOrBookText = intl.formatMessage({
    id: 'T6WXv73p-6T4BbC00gd6',
    defaultMessage: 'Call or book online.',
    description: 'CTA heading on Maldives charter hero card when phone support available',
  })
  const bookOnlineText = intl.formatMessage({
    id: 'aXUCMjV0RDAMQsPg_Hu1',
    defaultMessage: 'Book online.',
    description: 'CTA heading on Maldives charter hero card for agent hub users',
  })
  const talkToExpertText = intl.formatMessage({
    id: 'fUr-763MCXn4G3kvNp3J',
    defaultMessage: 'Talk to a Maldives travel expert today.',
    description: 'Body text encouraging contact with Maldives travel expert on hero CTA card',
  })
  const callPriorityLineText = intl.formatMessage({
    id: 'fOrJfpCYvMdcnwNVq215',
    defaultMessage: 'Call our priority phone line',
    description: 'Bold text prompting to call Maldives charter priority phone line',
  })
  const browseAllOffersText = intl.formatMessage({
    id: 'sy5cTyKtunE9OMODsMqD',
    defaultMessage: 'Browse all offers',
    description: 'Button text to scroll to all Maldives charter offers',
  })
  const callNowText = intl.formatMessage({
    id: 'U06qjUmrf-T14_XZdqq1',
    defaultMessage: 'Call now',
    description: 'Mobile button text to call Maldives charter support',
  })
  const contactBannerText = intl.formatMessage({
    id: 'LBwcJkNj6L-_WNjHEcSh',
    defaultMessage: 'Our Maldives travel experts can help with any questions',
    description: 'Contact banner text for Maldives charter support in the maldives direct search page section',
  })
  const disclaimerText = intl.formatMessage({
    id: 'maNvMbV-W5sJorWfeNYJ',
    defaultMessage: '*Based on average time savings compared with non-direct flights from Melbourne to Malé operated by competitor airlines. Calculation includes estimated total door-to-door travel time, encompassing flight duration, transfers, layovers and hotel check-in/check-out times. Subject to government & regulatory approvals.',
    description: 'Legal disclaimer about charter flight transit time savings comparison',
  })
  const selectTripDatesText = intl.formatMessage({
    id: 'oU0vVHgO-s0CqiSIjI2l',
    defaultMessage: 'Select your trip dates',
    description: 'Heading title for Maldives charter flight calendar modal',
  })

  if (!maldivesCharterEnabled) {
    // show 404 error page
    return <Error404PageWithOffers />
  }

  return (
    <div>
      <div ref={heroDetectorRef}>
        <StyledHeroImage
          id={MALDIVES_MARKETING_BANNER_IMAGE_ID}
          fit="bottom"
          gravity="center"
          mobileAspectRatio="7:6"
          tabletAspectRatio="9:5"
          desktopAspectRatio="18:5"
          largeDesktopAspectRatio="10:3"
        >
          <HeroContent>
            <HeroText>
              <Group direction="vertical" gap={8} tabletGap={24}>
                <Pill kind="primary" variant="success" style={{ alignSelf: 'flex-start' }}>
                  {exclusiveToLEText}
                </Pill>

                <VerticalSpacer gap={8}>
                  <CSSBreakpoint max="mobile">
                    <Heading variant="heading2" as="h1" colour="neutral-eight">
                      {heroHeadingMessage}
                    </Heading>
                  </CSSBreakpoint>
                  <CSSBreakpoint min="tablet">
                    <Heading variant="heading1" as="h1" colour="neutral-eight">
                      {heroHeadingMessage}
                    </Heading>
                  </CSSBreakpoint>
                  <BodyText variant="large" colour="neutral-eight" weight="bold">
                    <FormattedMessage
                      id="wT2Rue_oSOoHDE7wrr51"
                      defaultMessage="Kids Fly FREE! Save over $1000 per child^. Hurry, limited time only!*"
                      description="Secondary hero text promoting kids fly free offer on Maldives Direct search page"
                    />
                  </BodyText>
                </VerticalSpacer>
              </Group>
            </HeroText>

            <CSSBreakpoint min="tablet">
              <CTACard padding={20}>
                <Group direction="vertical" gap={8} tabletGap={16}>
                  <Group direction="vertical" gap={8} horizontalAlign="center">
                    <Group direction="vertical" gap={8} horizontalAlign="center" desktopDirection="horizontal">
                      <Heading variant="heading4" as="h4" align="center" colour="highlight-secondary">
                        {hurryLimitedSeatsText}
                      </Heading>
                      <Heading variant="heading4" as="h4" align="center" colour="neutral-one">
                        {!isAgentHubEnabled ? callOrBookText : bookOnlineText}
                      </Heading>
                    </Group>
                    {!isAgentHubEnabled && (
                      <>
                        <Group direction="vertical" gap={0} horizontalAlign="center">
                          <BodyText variant="large" align="center">
                            {talkToExpertText}
                          </BodyText>
                          <BodyText variant="large" align="center" weight="bold">
                            {callPriorityLineText}
                          </BodyText>
                        </Group>

                        <MaldivesSupportPhoneNumber headingVariant="heading3" />
                      </>
                    )}
                  </Group>

                  <TextButton kind="primary" size="large" fit="full-width" onClick={handleBrowseAllOffersClick}>
                    {browseAllOffersText}
                  </TextButton>
                </Group>
              </CTACard>
            </CSSBreakpoint>
          </HeroContent>

          <CSSBreakpoint max="mobile">
            <CTACardMobile padding={20}>
              <Group direction="vertical" gap={8} tabletGap={16}>
                <Group direction="vertical" gap={12} horizontalAlign="center">
                  <Heading variant="heading4" as="h4" colour="highlight-secondary">
                    <FormattedMessage
                      id="3PBCEvpwInZOcUYT19am"
                      defaultMessage="<primary>Hurry, limited seats!</primary> <secondary>Call or book online.</secondary>"
                      description="Mobile CTA heading with urgency message and call-to-action on Maldives charter page"
                      values={{
                        primary: (chunks) => <BodyText variant="large" as="span" colour="highlight-secondary">{chunks}</BodyText>,
                        secondary: (chunks) => <BodyText variant="large" as="span" colour="neutral-one">{chunks}</BodyText>,
                      }}
                    />
                  </Heading>
                  <Group direction="horizontal" gap={8} horizontalAlign="stretch" fullWidth verticalAlign="center">
                    <TextButton
                      kind="secondary"
                      variant="dark"
                      startIcon={<LinePhoneIcon />}
                      href={MALDIVES_CHARTER_PRIORITY_SUPPORT_NUMBER_HREF}
                      rel="nofollow"
                      fit="full-width"
                    >
                      {callNowText}
                    </TextButton>
                    <TextButton kind="primary" fit="full-width" onClick={handleBrowseAllOffersClick}>
                      {browseAllOffersText}
                    </TextButton>
                  </Group>
                </Group>
              </Group>
            </CTACardMobile>
          </CSSBreakpoint>
        </StyledHeroImage>
      </div>
      {hasTourBundleOffers && (
        <CSSBreakpoint min="tablet">
          <LayoutContainer relative>
            <OfferModeToggleWrapper id="maldives-direct-mode-toggle-desktop" className={!heroInView ? 'full-size' : undefined}>
              <ModeToggleTabs
                variant="desktop"
                isTourBundleMode={isTourBundleMode}
                onHotelsModeClick={handleHotelsModeClick}
                onTourBundlesModeClick={handleTourBundlesModeClick}
                showFullSize={!heroInView}
                modeToggleScale={modeToggleScale}
                modeToggleRef={modeToggleRef}
              />
            </OfferModeToggleWrapper>
          </LayoutContainer>
          <ModeToggleBottomSpacer />
        </CSSBreakpoint>
      )}

      {/* USP Section */}
      <USPSection>
        <CSSBreakpoint min="tablet">
          <LayoutContainer>
            <VerticalSpacer gap={40}>
              <Heading variant="heading2" as="h2" align="start" tabletAlign="center">
                <FormattedMessage
                  id="C70J4bxwqoA1AzIjCwTP"
                  defaultMessage="Less time flying, {br}more time in paradise"
                  description="Unique selling proposition section heading text on the Maldives Direct search results page"
                  values={{
                    br: <br />,
                  }}
                />
              </Heading>

              <USPGrid>
                {displayedUspsCombined.map(uspItem => (
                  <CharterBundleLandingPageUspItem
                    key={uspItem.uspId}
                    title={uspItem.title}
                    description={uspItem.description}
                    icon={uspItem.icon}
                    tabletIcon={uspItem.tabletIcon}
                  />),
                )}
              </USPGrid>

              {!agentHubOrSubBrandConfig.isEnabled && <USPFooter direction="vertical" gap={8} verticalAlign="center" tabletDirection="horizontal" tabletGap={16} tabletHorizontalAlign="center">
                <BodyText variant="large">
                  <FormattedMessage
                    id="tzfCzmsUZ7nLhW1PkUqK"
                    defaultMessage="Our Maldives travel experts can help with any questions. <strong>Call our priority line</strong>"
                    description="USP footer text encouraging contact with Maldives charter experts"
                    values={{
                      strong: (chunks) => <strong>{chunks}</strong>,
                    }}
                  />
                </BodyText>
                <MaldivesSupportPhoneNumber />
              </USPFooter>}
            </VerticalSpacer>
          </LayoutContainer>
        </CSSBreakpoint>

        <CSSBreakpoint max="mobile">
          <LayoutContainer>
            <VerticalSpacer gap={16}>
              <Heading variant="heading3" as="h3" align="start" tabletAlign="center">
                <FormattedMessage
                  id="2Nm93gUJXNYfB8F6uwqa"
                  defaultMessage="Less time flying, {br}more time in paradise"
                  description="USP section heading on Maldives Direct search page - mobile"
                  values={{
                    br: <br />,
                  }}
                />
              </Heading>

              <Group direction="vertical" gap={4}>
                {displayedMobileUsps.map(uspItem => (
                  <BodyTextBlock variant="medium" key={uspItem.uspId} startIcon={uspItem.icon} colour="neutral-one">
                    {uspItem.description}
                  </BodyTextBlock>
                ))}
              </Group>

              <CharterPartnerIcon />
            </VerticalSpacer>
          </LayoutContainer>
        </CSSBreakpoint>
      </USPSection>
      {hasTourBundleOffers && (
        <MobileOfferModeToggleStickyWrapper max="mobile">
          <OfferModeToggleWrapper id="maldives-direct-mode-toggle-mobile">
            <ModeToggleTabs
              variant="mobile"
              isTourBundleMode={isTourBundleMode}
              onHotelsModeClick={handleHotelsModeClick}
              onTourBundlesModeClick={handleTourBundlesModeClick}
            />
          </OfferModeToggleWrapper>
        </MobileOfferModeToggleStickyWrapper>
      )}

      {/* Bundle Search Section */}
      {!isTourBundleMode && <MaldivesCharterFilters
        searchState={{
          rooms,
          selectedBundle,
          departureAirport: departureAirport,
          flightClass: fareClass,
          luxPlusEnabled,
          checkInDate,
          checkOutDate,
          departDate,
          returnDate,
        }}
        onRoomsChange={setRooms}
        onRoomsApply={checkAndResetFareClassIfNeeded}
        onBundleChange={handleBundleChange}
        onDateSelect={handleDateSelect}
        onAirportChange={handleAirportChange}
        onFlightClassChange={handleFlightClassChange}
        onOpenFlightCalendar={handleOpenFlightCalendar}
        availableFareClasses={availableFareClasses}
      />}

      {/* Offers Section - using same data flow as HotelSearchPage */}
      <OffersSection id={MALDIVES_DIRECT_OFFERS_SECTION_ID}>
        <OffersContainer>
          <VerticalSpacer gap={32}>
            {isTourBundleMode && (
              <Heading id="maldives-direct-search-filters-heading" variant="heading2" as="h2" align="center">
                {tourModeBundlesHeading}
              </Heading>
            )}
            {!isTourBundleMode && <ReturnCharterFlightTile
              route={MALDIVES_CHARTER_ROUTE}
              fareClass={fareClassForSearch}
              checkInDate={checkInDate}
              checkOutDate={checkOutDate}
              occupancies={rooms}
              flightOrigin={departureAirport.code === MELBOURNE_AIRPORT.code ? undefined : departureAirport}
            />}
            <OffersHeader direction="vertical" gap={8} tabletDirection="horizontal" tabletVerticalAlign="center" tabletHorizontalAlign="space-between">
              {isTourBundleMode ? tourOffersHeaderNode : offersHeaderNode}
              {!isTourBundleMode && <Group direction="horizontal" gap={8} verticalAlign="center" horizontalAlign="space-between">
                <OfferSortControl
                  sortOptions={maldivesSortOptions}
                  onSortChange={handleSortChange}
                />
                <HotelPriceToggle value={priceMode} onChange={handlePriceModeChange} />
              </Group>}
            </OffersHeader>
          </VerticalSpacer>
          {isTourBundleMode && (
            <OfferList
              filters={tourBundleFilters}
              tileStyle="search"
              pageSize={pageSize}
              showLoadingTiles={tourBundleOfferList.fetching || tourBundleOfferList.offerIds.length === 0}
              disableUseOfferList
              extraOfferListAndAction={{ action: OfferListAction.OVERRIDE, offerList: tourBundleOfferList }}
              overrideFetching={tourBundleOfferList.fetching}
              minRequiredOffers={pageSize}
            />
          )}
          {!isTourBundleMode && (
            <MaldivesOffersPricingContext.Provider value={offersPricingData}>
              <MaldivesDirectBundleProvider value={bundleContextValue}>
                <OfferList
                  filters={bundleFilters}
                  tileStyle="search"
                  pageSize={pageSize}
                  showLoadingTiles={isOffersLoading || displayOfferList.offerIds.length === 0}
                  disableUseOfferList
                  extraOfferListAndAction={{ action: OfferListAction.OVERRIDE, offerList: displayOfferList }}
                  overrideFetching={isOffersLoading}
                  minRequiredOffers={pageSize}
                />
              </MaldivesDirectBundleProvider>
            </MaldivesOffersPricingContext.Provider>
          )}
        </OffersContainer>
      </OffersSection>

      <DreamBlock
        tiles={MALDIVES_DREAM_TILES}
        title={intl.formatMessage({
          id: 'V-qmg9wDsPt0QYJmClE6',
          defaultMessage: 'Dream about the Maldives',
          description: 'Title for the dream block section on Maldives Direct search page',
        })}
      />

      {/* Contact Information Banner */}
      <ContactBannerContainer>
        <Group direction="vertical" gap={12}>
          <Group direction="vertical" tabletDirection="horizontal" tabletHorizontalAlign="center" verticalAlign="center" gap={16}>
            <BodyText variant="large">
              {contactBannerText}
            </BodyText>
            <MaldivesSupportPhoneNumber />
          </Group>
          <BodyText variant="small" colour="neutral-two">
            {disclaimerText}
          </BodyText>
          {!isTourBundleModeRequested && <KidsFlyFreeTermsAndConditions />}
        </Group>
      </ContactBannerContainer>

      {/* Flight Calendar Modal - Drawer on mobile, Default on tablet+ */}
      {/* Desktop: Show full calendar in modal */}
      {!isTourBundleMode && isDesktop && (
        <Modal
          isOpen={isFlightCalendarOpen}
          onClose={handleCloseFlightCalendar}
          title={
            <Group direction="horizontal" horizontalAlign="space-between" gap={16} verticalAlign="center" fullWidth>
              <span>{selectTripDatesText}</span>
              <CharterBundleCalendarActions
                nights={nights}
                onDurationChange={handleDurationChangeFromCalendar}
                durationOptions={charterBundleDurationOptions}
                luxPlusEnabled={luxPlusEnabled}
                onLuxPlusToggle={handleLuxPlusToggle}
                fareClass={fareClass}
                onFareClassChange={handleFlightClassChange}
                availableFareClasses={availableFareClasses}
              />
            </Group>
          }
          size="XL"
        >
          <MaldivesDirectSearchFlightCalendar
            onDateSelect={handleFlightDateSelect}
            nights={nights}
            occupancies={rooms}
            luxPlusEnabled={luxPlusEnabled}
            dateRangesByMonth={dateRangesByMonth}
            selectedMonth={selectedMonth}
            onMonthChange={handleMonthChange}
            fareClass={fareClassForSearch}
            priceMode={priceMode}
            selectedOptionId={`${checkInDate}-${nights}n`}
            mode="search"
            itemBundleDataByDate={offersPricingData.calendarPricingByDate}
          />
        </Modal>
      )}

      {/* Mobile: Show two-step calendar (month selection → date selection) */}
      {!isTourBundleMode && !isDesktop && (
        <Modal
          height="full"
          title={selectTripDatesText}
          isOpen={isFlightCalendarOpen}
          onClose={handleCloseFlightCalendar}
          isPrimaryActionHidden
          flushListItems
        >
          <MaldivesDirectSearchFlightCalendarMobile
            onDateSelect={handleFlightDateSelect}
            nights={nights}
            occupancies={rooms}
            luxPlusEnabled={luxPlusEnabled}
            dateRangesByMonth={dateRangesByMonth}
            selectedMonth={selectedMonth}
            onMonthChange={handleMonthChange}
            fareClass={fareClassForSearch}
            priceMode={priceMode}
            selectedOptionId={`${checkInDate}-${nights}n`}
            mode="search"
            itemBundleDataByDate={offersPricingData.calendarPricingByDate}
            headerActions={
              <CharterBundleCalendarActions
                nights={nights}
                onDurationChange={handleDurationChangeFromCalendar}
                durationOptions={charterBundleDurationOptions}
                luxPlusEnabled={luxPlusEnabled}
                onLuxPlusToggle={handleLuxPlusToggle}
                fareClass={fareClass}
                onFareClassChange={handleFlightClassChange}
                availableFareClasses={availableFareClasses}
              />
            }
          />
        </Modal>
      )}

      {/* Floating Call Prompt - appears when hero is out of view */}
      <MaldivesCallPrompt
        show={!heroInView && !isCallPromptClosed && !agentHubOrSubBrandConfig.isEnabled}
        onClose={() => setIsCallPromptClosed(true)}
      />
    </div>
  )
}

export default MaldivesDirectSearchPage
